/**
 * The account form's long-lived experiment slot. Both arms serve V2 today:
 * control holds the shipped baseline, treatment carries whatever variant is
 * under test, and between experiments the two coincide — which is what makes
 * moving the rollout invisible to users.
 *
 * Exported because surfaces outside `AccountForm`'s own gate — a standalone
 * module mount that has to move with the form — need the same key.
 */
export const ACCOUNT_FORM_FLAG = 'auth-fe-account-form' as const;

/**
 * Consumer-side flag choosing the V2 registration step's arm. **Control is the
 * password field** — cosmos production `/login` collects one, so collecting one
 * is parity. `"on"` runs the experiment arm, "register lite": no password, the
 * account finished through an emailed set-up link.
 *
 * Named for the arm it turns on, so the key reads the way it behaves — a
 * dashboard showing `register-lite: on` means the lite path is running. "Lite"
 * is the term the login-event analytics already uses for this credential shape
 * (LITE vs EP), so the arms report in the same vocabulary as the funnel.
 *
 * Wired by handing `AccountFormV2` a `registerLiteFlagProps` evaluator, which
 * holds the registration step behind a loader until the arm resolves. The
 * polarity matches the arm it selects, so there is nothing to invert. Unwired
 * means control, so a surface that never reads this flag renders production
 * behavior.
 *
 * Nested inside {@link ACCOUNT_FORM_FLAG}: it only has an effect where V2 is
 * already rendering, so it cannot reach anyone until the V2 ramp starts.
 */
export const REGISTER_LITE_FLAG = 'growth-26-08-register-lite' as const;

/**
 * Orders the provider buttons by the seller's client — Google first in Chrome
 * and on Android, Apple first in Safari and on Apple hardware, and the declared
 * order for any client the classifier declines to place.
 *
 * A rollout switch, not an experiment arm: nothing reports an exposure for it,
 * and `AccountForm` applies it to **both** arms of {@link ACCOUNT_FORM_FLAG},
 * because the ordering is a property of the surface rather than of whichever
 * variant that slot is testing. Turning it on therefore changes what the
 * control arm renders, which is a scheduling question rather than a code one.
 *
 * Wired by handing `AccountForm` a `clientOrderedProvidersFlagProps` evaluator,
 * the same injected-evaluator shape the other keys here take — auth-fe never
 * reads a flag source itself. Unwired means off, so a surface that passes no
 * evaluator renders the declared Google-above-Apple order and never pays for
 * the read.
 *
 * Lower precedence than the last-used method: where a seller's own last-used
 * provider is known it decides the stack, and this settles the order for
 * everyone else.
 */
export const CLIENT_ORDERED_PROVIDERS_FLAG = 'growth-26-08-client-ordered-providers' as const;
